import { useEffect, useState } from "react";
import { PiPhoneOutgoingFill } from "react-icons/pi";
import { FaPauseCircle, FaPhone } from "react-icons/fa";
import { TbGridDots, TbSunFilled } from "react-icons/tb";
import { FaFileLines, FaRegPaperPlane, FaStar, FaXmark, FaMoon, FaEye, FaComment, FaUser, FaShare } from "react-icons/fa6";
import moment from "moment";

// Misc
import { useAppSelector } from "src/store/hooks";

// Helpers
import { sleep, useTyping } from "src/helpers";

// Websockets
import { ws } from "src/services/ws";

// Panels
import PanelTransference from "../components/panels/panel-transference";
import Timer from "../../components/timer";
import PanelDialer from "../components/panels/panel-dialer";
import PanelStatus from "../components/panels/panel-status";
import PanelPrePause from "../components/panels/panel-pre-pause";
import PanelUda from "../components/panels/panel-user";

// Components
import ControlButton from "../components/control-button";

// Types
import { TabType } from "src/types";
import PSTNCall from "src/models/pstn-call";
import PanelScript from "../components/panels/panel-script";

export default function TextChat({ udaIsTyping, endCall, addMessage, setFinishStatus }: { udaIsTyping: boolean; addMessage: Function; endCall: Function; setFinishStatus: Function }) {
  const { callDetails, udaQueueId } = useAppSelector((state) => state.call);
  const { general } = useAppSelector((state) => state.config);
  const { userInfo } = useAppSelector((state) => state.auth);
  const [state, setState] = useState<{
    messageInput: string;
    tabOpened: TabType | null;
    darkMode: boolean;
    internalMessages: Array<any>;
  }>({
    messageInput: "",
    tabOpened: TabType.SCRIPT,
    darkMode: general.theme === "dark",
    internalMessages: [],
  });

  const wsBinds: Array<number> = [];

  // CARROSSEL
  const activeNumber = callDetails?.pstnNumbers.find((pstn) => pstn.active);
  const pstnLefover: PSTNCall[] = activeNumber && callDetails?.pstnNumbers.length ? callDetails?.pstnNumbers.filter((n) => n.internalId !== activeNumber.internalId && !n.called) : [];

  useEffect(() => {
    async function init() {
      // Add initial message
      addMessage(true, userInfo.name, `Oi ${callDetails!.uda.usuario.nome}, tudo bem? Como posso ajudar você?`);

      wsBinds.push(
        ws.on("UDA_ENDED_CALL", (data?: { endedBy?: string }) => {
          const origin = data?.endedBy === "fop" ? "fop" : undefined;
          endCall(false, undefined, origin);
        })
      );
    }
    init();

    return () => {
      ws.clearBinds(wsBinds);
    };
  }, []);

  // Hook typing
  useTyping(state.messageInput, () =>
    ws?.sendMessage({
      type: "PA_IS_TYPING",
      data: {
        udaQueueId: udaQueueId,
      },
    }),
  );

  // Auto Scroll Mmessages
  useEffect(() => {
    if (callDetails) {
      if (state.internalMessages.length < callDetails.messageList.length) {
        const newState: any = {};

        // Update
        newState.internalMessages = callDetails.messageList;

        setState((s) => ({ ...s, ...newState }));
      }

      // Scroll
      let elm = document.querySelector(".webchat--message-list");
      if (elm) elm.scrollTop = elm.scrollHeight;
    }
  }, [callDetails?.messageList]);

  const _addMessage = () => {
    if (state.messageInput.length) addMessage(true, userInfo.name, state.messageInput);
    setState((s) => ({ ...s, messageInput: "" }));
  };

  const toggleTab = async (tabDesired: TabType | null, callback?: Function) => {
    setState((s) => ({
      ...s,
      tabOpened: s.tabOpened === tabDesired ? null : tabDesired,
    }));

    await sleep(200);

    callback && callback();
  };

  const voipDialerInstance = callDetails?.dialerInstances?.find((i) => i.instanceName === "VOIP");

  return (
    <div className={`${state.darkMode && "dark"}  grow flex flex-col`}>
      <div className="text-chat-room dark:bg-neutral-gray-900 grow flex flex-col bg-white">
        <div className="text-chat-room--top grow flex px-8 pt-8 gap-6">
          <section className={`text-container gap-6 p-6 grow relative h-full max-w-2xl m-auto dark:bg-white bg-neutral-gray-100 rounded-lg overflow-hidden flex flex-col justify-center`}>
            <header className="flex justify-between">
              <h3 className="text-2xl font-bold">Chat</h3>

              <button type="button" onClick={() => endCall(true)} className="bg-transparent text-neutral-gray-400">
                <FaXmark size={24} />
              </button>
            </header>

            <ul className="webchat--message-list grow overflow-y-auto h-0 p-0 m-0 gap-6 flex flex-col">
              {callDetails?.messageList.map((message) => (
                <li key={Math.random()} className={`message-item-wrap block ${message.self ? "self" : ""}`}>
                  <div
                    className={`message-bag p-3 text-base mb-2 rounded-lg ${
                      message.self ? "bg-primary-blue-600 text-white rounded-ee-none" : "bg-secondary-blue text-black rounded-es-none"
                    }`}
                  >
                    {message.text}
                  </div>
                  <span className={`message-date block text-neutral-gray-900 w-fit ${message.self ? "ml-auto" : "mr-auto"}`}>
                    {moment(message.created_at).format("DD/MM/YYYY HH:mm")} ・ {message.self ? "(interprete)" : "(surdo)"}
                  </span>
                </li>
              ))}
            </ul>
            {udaIsTyping && <div className={`pa-typing w-3/4 p-3 text-base mb-2 rounded-lg bg-secondary-blue text-black mr-auto rounded-es-none`}>Digitando...</div>}

            <div className="webchat--commands flex gap-4">
              <textarea
                className="webchat--textarea grow border border-neutral-gray-200 py-3 px-4 rounded-lg"
                placeholder="Digite aqui"
                value={state.messageInput}
                onChange={(e) => setState((s) => ({ ...s, messageInput: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    return e.key === "Enter" && _addMessage();
                  }
                }}
                rows={1}
              />

              <button className="webchat--send-btn bg-primary-blue-400 text-white rounded-lg w-12 h-12 items-center justify-center flex" type="button" onClick={_addMessage}>
                <FaRegPaperPlane />
              </button>
            </div>
          </section>
          {/* Script */}
          {state.tabOpened === TabType.SCRIPT && <PanelScript callDetails={callDetails!} togglePanel={toggleTab} />}
          {state.tabOpened === TabType.DIALER && <PanelDialer fullHeight dialer={voipDialerInstance} togglePanel={toggleTab} />}
          {/* Transference panel */}
          {state.tabOpened === TabType.TRANSFERENCE && <PanelTransference togglePanel={toggleTab} />}
          {/* Status Panel */}
          {state.tabOpened === TabType.STATUS && (
            <PanelStatus selectedStatus={callDetails?.finishStatus} togglePanel={toggleTab} statusList={callDetails?.finishStatuses || []} setStatus={setFinishStatus} />
          )}
          {/* Prepause */}
          {state.tabOpened === TabType.PREPAUSE && <PanelPrePause togglePanel={toggleTab} />}
          {/* UDA */}
          {state.tabOpened === TabType.UDA && <PanelUda aderenteSlug={callDetails?.aderenteSlug!} servicoId={callDetails?.servico.id!} togglePanel={toggleTab} udaInfo={callDetails?.uda} />}
        </div>
        <div className="text-chat-room--bottom p-8 flex">
          <div className="uda-infos flex gap-6 items-center dark:text-white grow">
            <ControlButton isActive={state.tabOpened === TabType.UDA} onClick={() => toggleTab(TabType.UDA)}>
              <FaUser />
            </ControlButton>

            <div className="flex flex-col">
              <span className="uda-name font-bold">{callDetails?.uda.usuario.nome}</span>
              <span className="call-timer text-sm">
                <Timer />
              </span>
            </div>
          </div>

          {/* Other controls */}
          <div className="center-controls flex justify-center grow gap-[18px]">
            <ControlButton id="webchat-btn" isActive={true}>
              <FaComment size={22} />
            </ControlButton>

            {/* Script */}
            <ControlButton isActive={state.tabOpened === TabType.SCRIPT} onClick={() => toggleTab(TabType.SCRIPT)}>
              <FaFileLines size={22} />
            </ControlButton>

            {/* Dialer */}
            <ControlButton onClick={() => toggleTab(TabType.DIALER)} isActive={state.tabOpened === TabType.DIALER}>
              <TbGridDots size={22} />
            </ControlButton>

            {/* Transfer? */}
            <ControlButton isActive={state.tabOpened === TabType.TRANSFERENCE} onClick={() => toggleTab(TabType.TRANSFERENCE)}>
              <PiPhoneOutgoingFill size={22} />
            </ControlButton>

            {/* Status Finalizador */}
            {callDetails?.finishStatuses && callDetails.finishStatuses.length > 0 && (
              <ControlButton isActive={state.tabOpened === TabType.STATUS} onClick={() => toggleTab(TabType.STATUS)}>
                <FaStar size={22} />
              </ControlButton>
            )}
          </div>

          <div className="right-controls flex justify-end grow gap-[18px]">
            {/* Dark/Light Mode */}
            <ControlButton onClick={() => setState((s) => ({ ...s, darkMode: !s.darkMode }))} isActive={state.darkMode}>
              {state.darkMode ? <FaMoon size={22} /> : <TbSunFilled size={22} />}
            </ControlButton>

            {/* Pre pause */}
            <ControlButton name="pre-pause" isActive={state.tabOpened === TabType.PREPAUSE} onClick={() => toggleTab(TabType.PREPAUSE)}>
              <FaPauseCircle size={22} />
            </ControlButton>

            {/* ?? */}
            <ControlButton>
              <FaEye size={22} />
            </ControlButton>

            {/* Endcall */}
            {callDetails?.features.includes("CARROSSEL") && (pstnLefover.length || !activeNumber?.called) && !callDetails.transferenceStatus.isTransferenceOrigin ? (
              <ControlButton name="endCall" className="bg-[#FEB53C] text-white w-[100px] hover:bg-[#be8136] hover:text-white" onClick={() => endCall(true, true)}>
                <FaShare size={22} />
              </ControlButton>
            ) : (
              <ControlButton name="endCall" className="bg-feedback-negative-500 text-white w-[100px] hover:bg-red-800 hover:text-white" onClick={() => endCall(true)}>
                <FaPhone size={22} style={{ transform: "rotate(-135deg)" }} />
              </ControlButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
